import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { AlertItem } from './types';

interface AlertBannerProps {
  alerts: AlertItem[];
  providerId?: number;
}

// Safe sessionStorage reader — returns false if storage is unavailable
// (SSR, Safari Private Mode, sandboxed iframes, or restricted storage).
// Symmetrical with the write-side try/catch in the dismiss handler below.
const readDismissed = (key: string | null): boolean => {
  if (!key) return false;
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    return window.sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

export const AlertBanner = ({ alerts, providerId }: AlertBannerProps) => {
  const dismissKey = providerId != null ? `dashboard-alerts-dismissed-${providerId}` : null;
  const [dismissed, setDismissed] = useState(() => readDismissed(dismissKey));
  useEffect(() => {
    setDismissed(readDismissed(dismissKey));
  }, [dismissKey]);
  const navigate = useNavigate();

  if (!dismissKey || alerts.length === 0 || dismissed) return null;

  return (
    <div
      role="alert"
      className="bg-error-t-900 border-l-4 border-error-50 rounded-lg p-4 flex items-start justify-between gap-3 mb-4"
    >
      <div className="flex gap-1 items-start">
        <ExclamationTriangleIcon
          aria-hidden="true"
          className="size-5 text-error-50 shrink-0 mt-0.5"
        />
        <div>
          <p className="font-semibold text-[15px] text-error-50 mb-2">
            {alerts.length} {alerts.length === 1 ? 'item needs' : 'items need'} your attention
          </p>
          <div className="flex flex-wrap gap-2">
            {alerts.map((alert) => (
              <button
                key={`${alert.category}-${alert.message}`}
                type="button"
                className="bg-white border border-error-t-300 rounded-full px-3 py-0.5 text-xs text-error-200 hover:bg-error-t-900 cursor-pointer"
                onClick={() => navigate(alert.navigateTo)}
              >
                {alert.category}: {alert.message}
              </button>
            ))}
          </div>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss alerts"
        data-cy="dashboard-alert-dismiss"
        className="shrink-0 -m-1.5 p-1.5 rounded-full text-error-50 opacity-60 hover:opacity-100 hover:bg-error-t-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-error-50 cursor-pointer transition-opacity"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            if (dismissKey) sessionStorage.setItem(dismissKey, 'true');
          } catch {
            // sessionStorage can throw in private-browsing / sandboxed contexts;
            // we still want the banner to dismiss for the current render.
          }
          setDismissed(true);
        }}
      >
        <XMarkIcon aria-hidden="true" className="size-5 pointer-events-none" />
      </button>
    </div>
  );
};
